'use client'

import { useState } from 'react'

import { CancelTransactionAction } from '@marketplace-web/conversation-shipping/conversation-shipping-feature'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { navigateToPage } from '@marketplace-web/browser/browser-navigation-util'
import {
  OppositeUser,
  TransactionModel,
  AdditionalTransactionAction,
  ConversationActionModal,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { INBOX_URL } from '@marketplace-web/messaging/navigation-feature'
import { UserBlockAction } from '@marketplace-web/conversation-safety/conversation-safety-feature'

import DeleteConversationAction from '../DeleteConversationAction'
import MemberReportAction from '../MemberReportAction'
import HelpAction from '../HelpAction'

type Props = {
  transaction?: TransactionModel | null
  conversationId?: number | string
  oppositeUser?: OppositeUser | null
  userId?: number
}

const ConversationActions = ({ transaction, conversationId, oppositeUser, userId }: Props) => {
  const { conversation } = useConversationContext()

  const isConversationDeletionRestricted = conversation?.isDeletionRestricted

  const [visibleModalName, setVisibleModalName] = useState<ConversationActionModal | null>(null)

  const closeModals = () => {
    setVisibleModalName(null)
  }

  const handleDeleteConversationClick = () => {
    setVisibleModalName(ConversationActionModal.DeleteConversation)
  }

  const handleDeleteConversation = () => {
    closeModals()

    navigateToPage(INBOX_URL)
  }

  const isDeleteConversationActionAvailable =
    !transaction ||
    transaction?.availableActions?.includes(AdditionalTransactionAction.DeleteConversation)

  const isReportingRestrictedForUser = oppositeUser?.isSystem || oppositeUser?.isModerator

  return (
    <>
      <HelpAction transactionId={transaction?.id} />
      <CancelTransactionAction
        conversation={conversation}
        userId={userId}
        visibleModalName={visibleModalName}
        onSetVisibleModalName={setVisibleModalName}
      />
      {isDeleteConversationActionAvailable && !isReportingRestrictedForUser && conversationId && (
        <MemberReportAction
          conversationId={conversationId}
          userSide={conversation?.transaction?.userSide ?? undefined}
        />
      )}
      {isDeleteConversationActionAvailable && conversationId && (
        <DeleteConversationAction
          conversationId={conversationId}
          onActionClick={handleDeleteConversationClick}
          onCancel={closeModals}
          onConfirm={handleDeleteConversation}
          restricted={Boolean(isConversationDeletionRestricted)}
          showModal={visibleModalName === ConversationActionModal.DeleteConversation}
        />
      )}
      <UserBlockAction
        oppositeUser={oppositeUser}
        userId={userId}
        visibleModalName={visibleModalName}
        onSetVisibleModalName={setVisibleModalName}
      />
    </>
  )
}

export default ConversationActions
